import { useState, useEffect, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@shared/lib/utils';
import { DetailEvents } from '@shared/DetailEvents';

import { errorsSelectors, showInfoSelectors, venueInfoSelectors } from './store/selectors';
import { useAppDispatch, useAppSelector } from '@core';
import { setSelectedDate } from './store/slice';
import { markStepCompleted } from '@core';
import { AppRoutes } from '@core';
import { fetchShowAndVenueInfo } from './store/actions';
import { urlParamsDataSelectors } from '../seatSelection/store/selectors';
import { clearSelectedSeats } from '../seatSelection/store/slice';
import { EventsList } from './components/EventsList/EventsList';
import { ShowAllButton } from './components/ShowAllButton';
import { API_DOCS, SUBAGENT_AGREEMENT_SHOW_ID } from '../checkout/consts/apiDocs.const';

export const DateSelection: FC = () => {
  const dispatch = useAppDispatch();
  const showInfo = useAppSelector(showInfoSelectors);
  const error = useAppSelector(errorsSelectors);
  const urlParamsData = useAppSelector(urlParamsDataSelectors);
  const venueInfo = useAppSelector(venueInfoSelectors);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  const handleSelectDate = (date: string, time: string, eventId: number) => {
    dispatch(
      setSelectedDate({
        date,
        time,
        eventId,
      })
    );
    handleNextStep();
  };

  // Вынесенная функция для перехода к следующему шагу
  const handleNextStep = () => {
    dispatch(markStepCompleted(AppRoutes.DATE_SELECTION));
    navigate(AppRoutes.SEATS_SELECTION);
  };

  useEffect(() => {
    // Страховка для browser Back / мобильной навигации / прямого перехода на первый шаг.
    // UI-переходы с выбранными местами подтверждаются отдельным диалогом до открытия этой страницы.
    dispatch(clearSelectedSeats());
  }, [dispatch]);

  useEffect(() => {
    if (!urlParamsData.showIds) return;
    if (showInfo?.id === urlParamsData.showIds) return; // данные уже загружены для этого шоу

    dispatch(fetchShowAndVenueInfo({ showId: urlParamsData.showIds }));
  }, [dispatch, urlParamsData.showIds, showInfo?.id]);

  // Компонент ошибки
  if (error) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-200px)]">
        <p className="text-status-error">Ошибка: {error}</p>
      </div>
    );
  }

  return (
    <>
      <h1 className={cn('text-2xl font-bold mt-2')}>{showInfo?.name}</h1>
      <div className="w-full">
        <div className="p-0">
          <h2 className="text-2xl font-semibold">Расписание</h2>
        </div>

        <EventsList
          showInfo={showInfo}
          venueInfo={venueInfo}
          showAll={showAll}
          handleSelectDate={handleSelectDate}
        />

        {(showInfo?.events?.length || 0) > 8 && !showAll && (
          <ShowAllButton onClick={() => setShowAll(true)} />
        )}
      </div>
      {showInfo && (
        <div className="mt-6">
          <DetailEvents key={showInfo.id} isItFirstStep={true} venueInfo={venueInfo} />
        </div>
      )}
      {/* TODO: footer вынести в отдельный компонент */}
      <div className="mt-6">
        <div className="border-t border-separator" />
        <div className="mt-3 text-center text-xs text-text-muted space-x-3">
          <a
            href={API_DOCS.CONDITIONS}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-text-link-hover hover:underline"
          >
            Способы оплаты, доставки и возврата
          </a>
          <span className="text-text-secondary">•</span>
          <a
            href={API_DOCS.COMPANY_INFO}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-text-link-hover hover:underline"
          >
            Реквизиты
          </a>
          <span className="text-text-secondary">•</span>
          <a
            href={API_DOCS.AGREEMENT}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-text-link-hover hover:underline"
          >
            Пользовательское соглашение
          </a>
          <span className="text-text-secondary">•</span>
          <a
            href={API_DOCS.OFFER}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-text-link-hover hover:underline"
          >
            Оферта
          </a>
          <span className="text-text-secondary">•</span>
          <a
            href={API_DOCS.POLITIC}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-text-link-hover hover:underline"
          >
            Политика конфиденциальности
          </a>
          {urlParamsData.showIds === SUBAGENT_AGREEMENT_SHOW_ID && (
            <>
              <span className="text-text-secondary">•</span>
              <a
                href={API_DOCS.PARTNERDOC}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-link hover:text-text-link-hover hover:underline"
              >
                Субагентский договор
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
};
